import { ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import logoEspaceClientTexte from '../../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../../images/Logo-Espace-client-texte-dark.png'
import { LEGAL_INCOMPLETE, LEGAL_PAGES, LEGAL_UPDATED_AT } from './legalEntity'

interface LegalLayoutProps {
  title: string
  /** Short summary used as the page meta description. */
  description: string
  children: ReactNode
}

/**
 * Shared shell for the legal pages: landing-style header, typographic body and cross-links.
 *
 * The documents are written in French only — they are governed by French law and the French
 * version is the only binding one. A notice is shown when the UI is set to another language
 * rather than leaving an English-speaking reader in front of an unexplained French wall.
 */
export default function LegalLayout({ title, description, children }: LegalLayoutProps) {
  const { t, i18n } = useTranslation()

  // The SPA has no head manager; set the title and description per page so the documents are
  // identifiable in search results and browser history.
  useEffect(() => {
    const previousTitle = document.title
    document.title = `${title} — Espace Client`
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute('content') ?? null
    meta?.setAttribute('content', description)
    return () => {
      document.title = previousTitle
      if (meta && previousDescription !== null) meta.setAttribute('content', previousDescription)
    }
  }, [title, description])

  return (
    <div className="landing-brand min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-100 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            aria-label="Espace Client — accueil"
            title="Espace Client — accueil"
            className="group flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <img
              src={logoEspaceClientTexte}
              alt="Espace Client"
              className="h-9 w-auto transition-transform group-hover:scale-105 dark:hidden"
            />
            <img
              src={logoEspaceClientTexteDark}
              alt=""
              aria-hidden="true"
              className="hidden h-9 w-auto transition-transform group-hover:scale-105 dark:block"
            />
          </Link>
          <Link
            to="/"
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            ← Accueil
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10 text-gray-700 dark:text-gray-300">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Dernière mise à jour : {LEGAL_UPDATED_AT}
        </p>

        {/* startsWith, not equality: the detector can resolve a regional code such as fr-FR. */}
        {!i18n.language.startsWith('fr') && (
          <p className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
            These legal documents are available in French only. As they are governed by French law,
            the French version is the sole binding one.
          </p>
        )}

        {LEGAL_INCOMPLETE && (
          <p className="mt-6 flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Les informations entre crochets [ ] doivent être complétées par l’éditeur avant la
              mise en production.
            </span>
          </p>
        )}

        {/* Typography is applied here so each document stays plain semantic HTML. */}
        <div
          className="mt-8 text-[0.95rem] leading-relaxed
            [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 dark:[&_h2]:text-white
            [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 dark:[&_h3]:text-white
            [&_p]:mb-4
            [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2
            [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2
            [&_strong]:font-semibold [&_strong]:text-gray-900 dark:[&_strong]:text-white
            [&_a]:text-brand-600 dark:[&_a]:text-brand-400 [&_a]:underline [&_a]:underline-offset-2"
        >
          {children}
        </div>
      </main>

      <footer className="py-8 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <nav className="max-w-3xl mx-auto px-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
          {LEGAL_PAGES.map((page) => (
            <Link
              key={page.path}
              to={page.path}
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {t(page.labelKey)}
            </Link>
          ))}
        </nav>
      </footer>
    </div>
  )
}
